import { api } from "../../../lib/api.js";

/**
 * Releases client.
 *
 * SOURCE OF TRUTH = the Support Center's staff releases controller
 * (`Api::V1::Admin::ReleasesController`), reached via the dashboard
 * server's documentation proxy (`/api/documentation/admin/releases*`,
 * `admin:users`-gated, dual-key). Its `release_notes` markdown is the
 * version's CHANGE LOG and the only copy of the release notes — it was
 * projected into a "Release Notes" doc folder until 2026-09-16, when that
 * projection was removed for putting the same text on the page twice. List shape is `{ data: [...] }`; create/update take
 * `{ release: {...} }`.
 *
 * NOTE: the 2026-05-18 unification spec moves Releases into crm-api
 * (product-scoped, `/api/crm/releases*`). That migration ("CRM
 * releases phase 1") is NOT on brain `main` yet — the deployed crm-api
 * has no `/api/v1/releases` route, so pointing here at `/api/crm`
 * returned **404** ("Could not load releases: CRM API returned 404")
 * and broke the whole System Admin ▸ Products & Releases panel. Until
 * that crm-api work ships + deploys, releases use the Support Center
 * endpoint that actually exists in production. When phase-1 lands,
 * flip BASE back to `/api/crm` and restore product scoping.
 */
export interface ReleaseDTO {
  id: number;
  product_id: number | null;
  product_name: string | null;
  version: string;
  status: string;
  release_notes: string | null;
  description: string | null;
  start_date: string | null;
  release_date: string | null;
  released: boolean;
  created_at: string;
}

export interface ReleaseInput {
  version: string;
  status: string;
  description?: string;
  release_notes?: string;
  start_date?: string | null;
  release_date?: string | null;
}

const BASE = "/api/documentation/admin";

export const RELEASE_STATUSES = [
  "planned",
  "in_progress",
  "released",
  "archived",
] as const;

export async function listReleases(_productId?: number): Promise<ReleaseDTO[]> {
  // Support Center releases are a single timeline (not product-scoped).
  // `_productId` is accepted for caller compatibility but unused until
  // the crm-api product-scoped migration deploys.
  const res = await api<{ data?: ReleaseDTO[]; releases?: ReleaseDTO[] }>(
    `${BASE}/releases`,
  );
  return res.data ?? res.releases ?? [];
}

export function createRelease(
  _productId: number,
  input: ReleaseInput,
): Promise<unknown> {
  return api(`${BASE}/releases`, {
    method: "POST",
    body: { release: input },
  });
}

export function updateRelease(
  id: number,
  input: Partial<ReleaseInput>,
): Promise<unknown> {
  return api(`${BASE}/releases/${id}`, {
    method: "PATCH",
    body: { release: input },
  });
}

export function publishRelease(id: number): Promise<unknown> {
  return api(`${BASE}/releases/${id}/publish`, { method: "POST" });
}

export function deleteRelease(id: number): Promise<unknown> {
  return api(`${BASE}/releases/${id}`, { method: "DELETE" });
}
